import axiosInstance from "../../lib/axiosInstance";
import { toast } from "react-toastify";
import { AppDispatch, RootState } from "../store";
import { quotationActions } from "../slices/quotation";
import { QuotationMapper } from "../../mappers/QuotationMapper";
import { getErrorMessage } from "../../utils/errorUtils";
import { ProductStub } from "./invoiceActions";

import { QuotationRequestDto } from "../../shared/dtos/quotation/QuotationRequestDto";
import { QuotationResponseDto } from "../../shared/dtos/quotation/QuotationResponseDto";
import { QuotationSummaryDto } from "../../shared/dtos/quotation/QuotationSummaryDto";
import { QuotationQueryDto } from "../../shared/dtos/quotation/QuotationQueryDto";
import { QuotationUpdateDto } from "../../shared/dtos/quotation/QuotationUpdateDto";
import { ConvertQuoteToInvoiceRequestDto } from "../../shared/dtos/quotation/ConvertQuoteToInvoiceRequestDto";
import { DocumentStatusRequestDto } from "../../shared/dtos/common/DocumentStatusRequestDto";
import { InvoiceResponseDto } from "../../shared/dtos/invoice/InvoiceResponseDto";
import { EmailRequestDto } from "../../shared/dtos/common/EmailRequestDto";
import { ApiResponseDto } from "../../shared/dtos/common/ApiResponseDto";
import { PaginatedResponse } from "../../shared/dtos/common/PaginatedResponse";
import { ApiResponseStatus } from "../../shared/enums/ApiResponseStatus";
import { SaleType } from "../../shared/enums/SaleType";
import { DocumentStatus } from "../../shared/enums/DocumentStatus";

export const getQuotations =
  (params?: QuotationQueryDto) =>
  async (dispatch: AppDispatch): Promise<void> => {
    dispatch(quotationActions.setLoadingFetchAll());
    try {
      const { data } = await axiosInstance.get<
        ApiResponseDto<PaginatedResponse<QuotationSummaryDto>>
      >("/quotations", {
        headers: { requiresAuth: true },
        params,
      });
      if (data.status === ApiResponseStatus.SUCCESS && data.data) {
        dispatch(quotationActions.setItems(data.data.content));
        dispatch(quotationActions.setPagination(data.data.pagination));
      }
    } catch (error) {
      dispatch(quotationActions.setErrorFetchAll(getErrorMessage(error)));
    }
  };

export const getQuotationById =
  (id: string) =>
  async (dispatch: AppDispatch): Promise<void> => {
    dispatch(quotationActions.setLoadingFetchOne());
    try {
      const { data } = await axiosInstance.get<
        ApiResponseDto<QuotationResponseDto>
      >(`/quotations/${id}`, {
        headers: { requiresAuth: true },
      });
      if (data.status === ApiResponseStatus.SUCCESS && data.data) {
        dispatch(quotationActions.setItem(data.data));
      }
    } catch (error) {
      dispatch(quotationActions.setErrorFetchOne(getErrorMessage(error)));
    }
  };

export const createQuotation =
  (payload: QuotationRequestDto) =>
  async (dispatch: AppDispatch): Promise<void> => {
    dispatch(quotationActions.setLoadingCreate());
    try {
      const { data } = await axiosInstance.post<
        ApiResponseDto<QuotationResponseDto>
      >("/quotations", payload, {
        headers: { requiresAuth: true },
      });
      if (data.status === ApiResponseStatus.SUCCESS && data.data) {
        toast.success(data.message || "Quotation generated successfully!");
        const summary = QuotationMapper.mapQuotationToSummary(data.data);
        if (summary) dispatch(quotationActions.createSuccess(summary));
        dispatch(quotationActions.clearQuoteCart());
        dispatch(
          quotationActions.setServerStatus({
            operation: "create",
            status: data.statusCode || 201,
            metadata: { newId: data.data.id },
          }),
        );
      }
    } catch (error) {
      const message = getErrorMessage(error);
      toast.error(message);
      dispatch(quotationActions.setErrorCreate(message));
    }
  };

export const updateQuotation =
  (id: string, payload: QuotationUpdateDto) =>
  async (dispatch: AppDispatch): Promise<void> => {
    dispatch(quotationActions.setLoadingUpdate());
    try {
      const { data } = await axiosInstance.put<
        ApiResponseDto<QuotationResponseDto>
      >(`/quotations/${id}`, payload, {
        headers: { requiresAuth: true },
      });
      if (data.status === ApiResponseStatus.SUCCESS && data.data) {
        toast.success("Quotation updated successfully!");
        const summary = QuotationMapper.mapQuotationToSummary(data.data);
        if (summary) dispatch(quotationActions.updateSuccess(summary));
        dispatch(quotationActions.setItem(data.data));
        dispatch(
          quotationActions.setServerStatus({
            operation: "update",
            status: data.statusCode || 200,
          }),
        );
      }
    } catch (error) {
      const message = getErrorMessage(error);
      toast.error(message);
      dispatch(quotationActions.setErrorUpdate(message));
    }
  };

export const deleteQuotation =
  (id: string) =>
  async (dispatch: AppDispatch): Promise<void> => {
    dispatch(quotationActions.setLoadingDelete());
    try {
      const { data } = await axiosInstance.delete<ApiResponseDto<void>>(
        `/quotations/${id}`,
        {
          headers: { requiresAuth: true },
        },
      );
      if (data.status === ApiResponseStatus.SUCCESS) {
        toast.success("Quotation removed successfully!");
        dispatch(quotationActions.deleteSuccess(id));
        dispatch(
          quotationActions.setServerStatus({
            operation: "delete",
            status: data.statusCode || 200,
          }),
        );
      }
    } catch (error) {
      const message = getErrorMessage(error);
      toast.error(message);
      dispatch(quotationActions.setErrorDelete(message));
    }
  };

export const updateQuotationStatus =
  (id: string, status: DocumentStatus) =>
  async (dispatch: AppDispatch): Promise<void> => {
    dispatch(quotationActions.setLoadingStatusUpdate());
    try {
      const payload: DocumentStatusRequestDto = { status };
      const { data } = await axiosInstance.post<ApiResponseDto<void>>(
        `/quotations/status/${id}`,
        payload,
        {
          headers: { requiresAuth: true },
        },
      );

      if (data.status === ApiResponseStatus.SUCCESS) {
        toast.success(`Quote status updated to ${status}`);
        dispatch(
          quotationActions.setServerStatus({
            operation: "status",
            status: data.statusCode || 200,
          }),
        );
      }
    } catch (error) {
      const message = getErrorMessage(error);
      toast.error(message);
      dispatch(quotationActions.setErrorStatusUpdate(message));
    }
  };

export const convertToInvoice =
  (id: string, shiftId: string) =>
  async (dispatch: AppDispatch): Promise<void> => {
    dispatch(quotationActions.setLoadingConvert());
    try {
      const payload: ConvertQuoteToInvoiceRequestDto = { shiftId };
      const { data } = await axiosInstance.post<
        ApiResponseDto<InvoiceResponseDto>
      >(`/quotations/convert-to-invoice/${id}`, payload, {
        headers: { requiresAuth: true },
      });
      if (data.status === ApiResponseStatus.SUCCESS && data.data) {
        toast.success("Successfully converted to invoice!");
        dispatch(
          quotationActions.setServerStatus({
            operation: "convert",
            status: 201,
            metadata: { invoiceId: data.data.id },
          }),
        );
      }
    } catch (error) {
      const message = getErrorMessage(error);
      toast.error(message);
      dispatch(quotationActions.setErrorConvert(message));
    }
  };

export const getQuotationPdf =
  (id: string) =>
  async (dispatch: AppDispatch): Promise<void> => {
    dispatch(quotationActions.setLoadingPdf());
    try {
      const { data } = await axiosInstance.get<Blob>(`/quotations/pdf/${id}`, {
        headers: { requiresAuth: true },
        responseType: "blob",
      });
      const pdfBlobUrl = window.URL.createObjectURL(
        new Blob([data], { type: "application/pdf" }),
      );
      dispatch(quotationActions.setPdfUrl(pdfBlobUrl));
    } catch (error) {
      const message = getErrorMessage(error);
      toast.error("Failed to load PDF preview: " + message);
      dispatch(quotationActions.setErrorPdf(message));
    }
  };

export const emailQuotation =
  (id: string, emailPayload: EmailRequestDto) =>
  async (dispatch: AppDispatch): Promise<boolean> => {
    dispatch(quotationActions.setLoadingEmail());
    try {
      const { data } = await axiosInstance.post<ApiResponseDto<void>>(
        `/quotations/email/${id}`,
        emailPayload,
        {
          headers: { requiresAuth: true },
        },
      );
      if (data.status === ApiResponseStatus.SUCCESS) {
        toast.success(data.message || "Quotation emailed successfully!");
        dispatch(quotationActions.setEmailSuccess());
        return true;
      }
      return false;
    } catch (error) {
      const message = getErrorMessage(error);
      toast.error(message);
      dispatch(quotationActions.setErrorEmail(message));
      return false;
    }
  };

export const resetQuoteServerStatus =
  () =>
  (dispatch: AppDispatch): void => {
    dispatch(quotationActions.setServerStatus(null));
  };

// --- Cart Actions ---
export const addQuoteItem =
  (product: ProductStub) =>
  (dispatch: AppDispatch, getState: () => RootState): void => {
    const { saleType } = getState().quotation;
    const price =
      saleType === SaleType.WHOLESALE
        ? product.wholesalePrice
        : product.retailPrice;
    dispatch(
      quotationActions.quoteItemAdd({
        itemId: product.id,
        name: product.name,
        brand: product.brandName,
        category: product.categoryName,
        price: price,
        discount: 0,
        qty: 1,
      }),
    );
  };

export const removeQuoteItem =
  (itemId: string) =>
  (dispatch: AppDispatch): void => {
    dispatch(quotationActions.quoteItemRemove(itemId));
  };

export const changeQuoteSaleType =
  (newType: SaleType) =>
  (dispatch: AppDispatch, getState: () => RootState): void => {
    const { cartItems } = getState().quotation;
    if (cartItems.length > 0) {
      toast.error(
        "Cannot switch sale types with items in the cart. Please clear the items first.",
      );
      return;
    }
    dispatch(quotationActions.setQuoteSaleType(newType));
  };

export const updateQuoteItemFields =
  (itemId: string, updates: any) =>
  (dispatch: AppDispatch): void => {
    dispatch(quotationActions.quoteItemUpdateFields({ itemId, updates }));
  };

export const quoteItemRemove = (itemId: string) => (dispatch: AppDispatch) =>
  dispatch(quotationActions.quoteItemRemove(itemId));
export const setQuoteCustomer = (customer: any) => (dispatch: AppDispatch) =>
  dispatch(quotationActions.setQuoteCustomer(customer));
export const clearQuoteCart = () => (dispatch: AppDispatch) =>
  dispatch(quotationActions.clearQuoteCart());
